"use client"

import { useState, useEffect, useRef, useId } from "react"
import Image from "next/image"
import { m } from "framer-motion"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import {
  CheckmarkIcon,
  CheckCircleOutlineIcon,
  ChevronRightIcon,
  CloseIcon,
  GreenCheck,
  InfoIcon,
  BodyFigureIcon,
  ChecklistIcon,
  Report2Icon,
  ReportIcon,
  FollowUpIcon,
  ArrowPathIcon,
  SwissShieldIcon,
} from "@/components/ui/icons"
import { PackageIcon } from "@/components/ui/package-icons"
import { CtaBanner } from "@/components/ui/cta-banner"
import { InsuranceSection } from "@/components/ui/insurance-section"
import { TestimonialsSection } from "../../_components/testimonials-section"
import type { Testimonial } from "@/lib/cms"
import { ReportMockupIpad } from "@/components/ui/report-mockup-ipad"
import { TypicalReportMockup } from "@/components/ui/typical-report-mockup"
import { ReportMockupIphone } from "@/components/ui/report-mockup-iphone"
import { FaqSection } from "@/components/ui/faq-section"
import { compareHealthCheckUpsKeys as k } from "@/i18n/compare-health-check-ups-switzerland-keys"
import { howItWorksKeys as hiwk } from "@/i18n/how-it-works-keys"
import { homeKeys as hk } from "@/i18n/home-keys"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { blobMediaUrl } from "@/lib/blob-url"
import { DoctorsSection } from "../../_components/doctors-section"
import type { ReviewerExpert } from "@/lib/reviewer-experts"

/* ───────────────────── CountUp ───────────────────── */

function CountUp({
  target,
  duration = 1500,
  className = "",
  suffix = "",
}: {
  target: number
  duration?: number
  className?: string
  suffix?: string
}) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const hasAnimated = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true
          const start = performance.now()
          const animate = (now: number) => {
            const progress = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - progress, 3)
            setCount(Math.round(eased * target))
            if (progress < 1) requestAnimationFrame(animate)
          }
          requestAnimationFrame(animate)
        }
      },
      { threshold: 0.3 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [target, duration])

  return (
    <span ref={ref} className={className}>
      {count}
      {suffix}
    </span>
  )
}

/* ───────────────────── Clear-signals gauge ─────────────────────
 * Segmented 270° arc — 4 segments, 3 olive (filled) + 1 gray (unfilled)
 * representing a 3/4 (in-range) glucose reading. Draws in on intersection.
 */

function ClearSignalsGauge() {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [drawn, setDrawn] = useState(false)

  useEffect(() => {
    const node = wrapperRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setDrawn(true)
          observer.disconnect()
        }
      },
      { threshold: 0.3 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const arcPath = "M 110 60 A 50 50 0 1 1 60 10"
  // 4 segments around the 270° arc, separated by 8-unit gaps (of 100 path units).
  // Total 4×19 + 3×8 = 100.
  const SEG = 19
  const GAP = 8
  // Olive paints only the first 3 segments — the trailing long gap (100) keeps
  // the 4th segment slot transparent so the gray track shows through.
  const filledDash = `${SEG} ${GAP} ${SEG} ${GAP} ${SEG} 100`
  // Unique mask id so the gauge can render multiple times on the page without collisions.
  const maskId = useId()

  return (
    <div
      ref={wrapperRef}
      className="relative flex h-48 w-48 items-center justify-center"
    >
      <svg
        viewBox="0 0 120 120"
        className="h-full w-full"
        style={{ transform: "rotate(135deg)" }}
      >
        <defs>
          <mask id={maskId}>
            {/* Single continuous arc that draws in from path start; reveals
                the segmented olive underneath as it advances. */}
            <path
              d={arcPath}
              pathLength={100}
              fill="none"
              stroke="white"
              strokeWidth="14"
              strokeLinecap="round"
              strokeDasharray="100"
              strokeDashoffset={drawn ? 0 : 100}
              style={{
                transition: "stroke-dashoffset 1.2s cubic-bezier(0, 0, 0.2, 1)",
              }}
            />
          </mask>
        </defs>
        {/* Track — all 4 segments in light gray, always visible */}
        <path
          d={arcPath}
          pathLength={100}
          fill="none"
          stroke="#e5e5e5"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${SEG} ${GAP}`}
        />
        {/* Fill — first 3 segments in olive, revealed by the mask */}
        <path
          d={arcPath}
          pathLength={100}
          fill="none"
          stroke="#A4B55E"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={filledDash}
          mask={`url(#${maskId})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="type-display-sm text-neutral-900">4.8</span>
        <span className="mt-0.5 text-xs tracking-wider text-neutral-400 uppercase">
          mmol/L
        </span>
      </div>
    </div>
  )
}

/* ───────────────────── Page ───────────────────── */

export default function CompareHealthCheckUpsSwitzerlandClient({
  initialExperts,
  initialFaqs,
  initialTestimonials = [],
}: {
  initialExperts?: ReviewerExpert[]
  initialFaqs?: {
    question: string
    answer: string
    slug?: string
    updatedAt?: string
    pages?: string[]
  }[]
  initialTestimonials?: Testimonial[]
} = {}) {
  const t = useTranslations("compareHealthCheckUps")
  const tReport = useTranslations("report")
  const tHiw = useTranslations("howItWorks")
  const tHome = useTranslations("home")
  const lp = useLocalePath()

  // Page-specific FAQs come from CMS (filtered by `pages: ["compare-health-check-ups-switzerland"]`).
  // The accordion heading still reuses the generic how-it-works "FAQ" label.
  const faqItems = initialFaqs ?? []

  return (
    <>
      {/* ─── Hero ─── */}
      {/* Top gradient softens the seam between the body's gray header zone
          (#f6f6f6 showing through the semi-transparent navbar pill) and the
          white hero. Fades over the first ~120px, then stays white. */}
      <section
        className="pt-12 pb-16 text-center md:pt-16"
        style={{
          backgroundImage:
            "linear-gradient(to bottom, #f6f6f6 0px, #ffffff 120px)",
        }}
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{t(k.pageLabel)}</p>

          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
            {t(k.heading)}
          </h1>

          <p className="type-body-lg mx-auto mt-4 text-balance text-neutral-600 md:max-w-[50%]">
            {t(k.subtitle)}
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
            <a
              href="#compare"
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white sm:w-auto">
                <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                  {t(k.compareOptions)}
                </span>
                <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                  <ArrowRightIcon size={18} />
                </span>
              </Button>
            </a>
            <Link
              href={lp("/pricing")}
              className="block w-full sm:inline-block sm:w-auto"
            >
              <Button variant="outline" className="w-full sm:w-auto">
                {t(k.bookCheckup)}
              </Button>
            </Link>
          </div>

          <div className="mx-auto mt-12 max-w-3xl border-t border-neutral-200 pt-8">
            <ul className="flex flex-col items-start gap-x-8 gap-y-3 sm:flex-row sm:flex-wrap sm:justify-center">
              {[k.heroBullet1, k.heroBullet2, k.heroBullet3].map((bk) => (
                <li
                  key={bk}
                  className="type-body-lg flex items-center gap-2 text-left text-neutral-900"
                >
                  <CheckCircleOutlineIcon size={22} className="shrink-0" />
                  <span>{t(bk)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ─── Why this matters ─── */}
      <section className="py-8 md:py-24">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="grid gap-10 md:grid-cols-3 md:gap-20">
            <div className="md:col-span-1">
              <h2 className="type-display-sm text-balance text-neutral-900">
                {t(k.whyHeading)}
              </h2>
              {/* Three stacked rows: eyebrow + question (where applicable) + body */}
              <div className="mt-10 space-y-10">
                <div>
                  <p className="type-label-md tracking-wider text-neutral-400 uppercase">
                    {t(k.whyAsksLabel)}
                  </p>
                  <p className="type-title-lg mt-3 text-balance text-neutral-900">
                    {t(k.whyAsksQuestion)}
                  </p>
                  <p className="type-body-lg mt-3 text-neutral-500">
                    {t(k.whyAsksBody)}
                  </p>
                </div>
                <div>
                  <p className="type-label-md tracking-wider text-neutral-400 uppercase">
                    {t(k.whyWantsLabel)}
                  </p>
                  <p className="type-title-lg mt-3 text-balance text-neutral-900">
                    {t(k.whyWantsQuestion)}
                  </p>
                  <p className="type-body-lg mt-3 text-neutral-500">
                    {t(k.whyWantsBody)}
                  </p>
                </div>
                <div>
                  <p className="type-body-lg text-neutral-600">{t(k.whyP3)}</p>
                </div>
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="ml-auto grid max-w-[700px] grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="type-body-lg bg-deep-blue-900 flex aspect-square flex-col justify-end rounded-[32px] p-8 text-white">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    {t(k.whyStat1Value)}
                  </p>
                  <p>{t(k.whyStat1Text)}</p>
                  <p className="mt-4 text-sm text-white/60">
                    {t.rich(k.whyStat1Source, {
                      lancet: (chunks) => (
                        <a
                          href="https://www.thelancet.com/journals/lancet/article/PIIS0140-6736(04)17018-9/abstract"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline decoration-white/40 hover:text-white/80"
                        >
                          {chunks}
                        </a>
                      ),
                    })}
                  </p>
                </div>

                <div className="type-body-lg bg-light-green-400 flex aspect-square flex-col justify-end rounded-[32px] p-8 text-neutral-900">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    {t(k.whyStat2Value)}
                  </p>
                  <p>{t(k.whyStat2Text)}</p>
                  <p className="mt-4 text-sm text-neutral-700">
                    {t(k.whyStat2Source)}
                  </p>
                </div>

                <div className="relative aspect-square overflow-hidden rounded-[32px]">
                  <Image
                    src={blobMediaUrl("/images/how-to-choose/why-portrait.jpg")}
                    alt={t(k.whyPortraitAlt)}
                    fill
                    sizes="(min-width: 768px) 33vw, 50vw"
                    className="object-cover"
                  />
                </div>

                <div className="type-body-lg bg-aubergine-900 flex aspect-square flex-col justify-end rounded-[32px] p-8 text-white">
                  <p className="type-display-lg mb-4 leading-none font-light">
                    {t(k.whyStat3Value)}
                  </p>
                  <p>{t(k.whyStat3Text)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Who is this for ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="mx-auto max-w-3xl text-center">
            <p className="type-label-lg text-neutral-400">{t(k.whoLabel)}</p>
            <h2 className="type-display-md mt-6 text-balance text-neutral-900">
              {t(k.whoHeading)}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                title: k.whoCard1Title,
                body: k.whoCard1Body,
                alt: k.whoCard1Alt,
                src: blobMediaUrl("/images/how-to-choose/who-baseline.jpg"),
              },
              {
                title: k.whoCard2Title,
                body: k.whoCard2Body,
                alt: k.whoCard2Alt,
                src: blobMediaUrl("/images/how-to-choose/who-family.jpg"),
              },
              {
                title: k.whoCard3Title,
                body: k.whoCard3Body,
                alt: k.whoCard3Alt,
                src: blobMediaUrl("/images/how-to-choose/who-gp.jpg"),
              },
              {
                title: k.whoCard4Title,
                body: k.whoCard4Body,
                alt: k.whoCard4Alt,
                src: blobMediaUrl("/images/how-to-choose/who-track.jpg"),
              },
            ].map((card) => (
              <div key={card.title}>
                <div className="relative aspect-[3/4] overflow-hidden rounded-[16px]">
                  <Image
                    src={card.src}
                    alt={t(card.alt)}
                    fill
                    sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                    className="object-cover"
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 backdrop-blur-md"
                    style={{
                      maskImage:
                        "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 40%, black 100%)",
                      WebkitMaskImage:
                        "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 40%, black 100%)",
                    }}
                  />
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
                    style={{
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                    <h3 className="type-title-lg">{t(card.title)}</h3>
                    <p className="type-body-lg mt-2 text-white/80">
                      {t(card.body)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── At-a-glance comparison ─── */}
      <section
        id="compare"
        aria-label="At-a-glance comparison"
        className="scroll-mt-24 pb-16"
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="rounded-[24px] bg-white px-4 py-12 md:px-10 md:py-16">
            <div className="mx-auto max-w-3xl text-center">
              <p className="type-label-lg text-neutral-400">
                {t(k.glanceLabel)}
              </p>
              {/* Line breaks come from `text-balance` automatically — the
                  translation no longer carries inline `<br>` markers. */}
              <h2 className="type-display-md mt-4 text-balance text-neutral-900">
                {t(k.glanceHeading)}
              </h2>
            </div>

            <div className="mt-10">
              {(() => {
                type GlanceCell =
                  | { kind: "text"; value: string }
                  | { kind: "check" }
                  | { kind: "cross" }

                const rows: {
                  crit: string
                  ahead: GlanceCell
                  typical: GlanceCell
                  gp: GlanceCell
                }[] = [
                  {
                    crit: t(k.glanceRow1Crit),
                    ahead: { kind: "text", value: t(k.glanceRow1Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow1Typical) },
                    gp: { kind: "text", value: t(k.glanceRow1Gp) },
                  },
                  {
                    crit: t(k.glanceRow2Crit),
                    ahead: { kind: "check" },
                    typical: { kind: "text", value: t(k.glanceRow2Typical) },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.glanceRow3Crit),
                    ahead: { kind: "text", value: t(k.glanceRow3Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow3Typical) },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.glanceRow4Crit),
                    ahead: { kind: "text", value: t(k.glanceRow4Ahead) },
                    typical: { kind: "cross" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.glanceRow5Crit),
                    ahead: { kind: "text", value: t(k.glanceRow5Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow5Typical) },
                    gp: { kind: "text", value: t(k.glanceRow5Gp) },
                  },
                  {
                    crit: t(k.glanceRow6Crit),
                    ahead: { kind: "text", value: t(k.glanceRow6Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow6Typical) },
                    gp: { kind: "text", value: t(k.glanceRow6Gp) },
                  },
                  {
                    crit: t(k.glanceRow7Crit),
                    ahead: { kind: "text", value: t(k.glanceRow7Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow7Typical) },
                    gp: { kind: "text", value: t(k.glanceRow7Gp) },
                  },
                  {
                    crit: t(k.glanceRow8Crit),
                    ahead: { kind: "text", value: t(k.glanceRow8Ahead) },
                    typical: { kind: "text", value: t(k.glanceRow8Typical) },
                    gp: { kind: "text", value: t(k.glanceRow8Gp) },
                  },
                  {
                    crit: t(k.glanceRow9Crit),
                    ahead: { kind: "check" },
                    typical: { kind: "text", value: t(k.glanceRow9Typical) },
                    gp: { kind: "text", value: t(k.glanceRow9Gp) },
                  },
                ]

                const renderCell = (cell: GlanceCell, highlighted: boolean) => {
                  if (cell.kind === "check") {
                    return <GreenCheck width={20} height={20} />
                  }
                  if (cell.kind === "cross") {
                    return (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle cx="10" cy="10" r="9.5" fill="#d4d4d4" />
                        <path
                          d="M7 7l6 6M13 7l-6 6"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    )
                  }
                  return (
                    <span
                      className={
                        highlighted
                          ? "type-body-lg text-light-green-600 font-medium"
                          : "type-body-lg text-neutral-600"
                      }
                    >
                      {cell.value}
                    </span>
                  )
                }

                const gridCols = "1.3fr 1.1fr 1.1fr 1.1fr"
                // Ahead column position within 4.6fr total (1.3/4.6 start, 1.1/4.6 width)
                const aheadLeftPct = (1.3 / 4.6) * 100
                const aheadWidthPct = (1.1 / 4.6) * 100

                return (
                  // Single table with horizontal scroll on narrow viewports.
                  // First column is sticky so the criterion stays visible while
                  // the user scrolls the product columns into view. Subtle
                  // right-shadow on the sticky column delineates it from the
                  // scrolling content (mobile only — desktop has no scroll).
                  <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
                    <div
                      className="relative min-w-[720px]"
                      style={{ gridTemplateColumns: gridCols }}
                    >
                      {/* Header row */}
                      <div
                        className="grid"
                        style={{ gridTemplateColumns: gridCols }}
                      >
                        <div />
                        <div className="bg-light-green-500 type-body-lg rounded-t-[16px] py-5 text-center text-white">
                          {t(k.glanceColAhead)}
                        </div>
                        <div className="type-body-lg py-5 text-center text-neutral-900">
                          {t(k.glanceColTypical)}
                        </div>
                        <div className="type-body-lg py-5 text-center text-neutral-900">
                          {t(k.glanceColGp)}
                        </div>
                      </div>

                      {/* Rows */}
                      <div>
                        {rows.map((row, i) => {
                          const rowBg =
                            i % 2 === 0 ? "bg-neutral-50" : "bg-white"
                          return (
                            <div
                              key={i}
                              className="grid"
                              style={{ gridTemplateColumns: gridCols }}
                            >
                              <div
                                className={`type-body-lg ${rowBg} flex items-center justify-start rounded-l-2xl px-4 py-4 text-left text-neutral-600`}
                              >
                                {row.crit}
                              </div>
                              <div
                                className={`${rowBg} flex items-center justify-center px-3 py-4 text-center`}
                              >
                                {renderCell(row.ahead, true)}
                              </div>
                              <div
                                className={`${rowBg} flex items-center justify-center px-3 py-4 text-center`}
                              >
                                {renderCell(row.typical, false)}
                              </div>
                              <div
                                className={`${rowBg} flex items-center justify-center rounded-r-2xl px-3 py-4 text-center`}
                              >
                                {renderCell(row.gp, false)}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Ahead column highlight — absolute overlay spanning header + rows.
                          Sits beneath the sticky first column (z-0) so the column always
                          covers it cleanly during horizontal scroll. */}
                      <div
                        className="border-light-green-500 pointer-events-none absolute inset-y-0 rounded-[16px] border-2"
                        style={{
                          left: `${aheadLeftPct}%`,
                          width: `${aheadWidthPct}%`,
                        }}
                      />
                    </div>
                  </div>
                )
              })()}
            </div>

            <div className="mt-6">
              <div className="type-body-sm flex gap-3 text-neutral-400">
                <InfoIcon size={18} className="shrink-0" />
                <p>{t(k.glanceNote)}</p>
              </div>
            </div>

            <div>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
                <Link
                  href={lp("/pricing")}
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white sm:w-auto">
                    <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                      {t(k.ctaBook)}
                    </span>
                    <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                      <ArrowRightIcon size={18} />
                    </span>
                  </Button>
                </Link>
                <Link
                  href={lp("/contact")}
                  className="block w-full sm:inline-block sm:w-auto"
                >
                  <Button variant="outline" className="w-full sm:w-auto">
                    {t(k.ctaTalk)}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Blood biomarker comparison (mirrors the at-a-glance, 2-column) ─── */}
      <section className="pb-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div className="rounded-[24px] px-4 py-12 md:px-10 md:py-16">
            <div className="mx-auto max-w-3xl text-center">
              <p className="type-label-lg tracking-wider text-neutral-400 uppercase">
                {t(k.bloodLabel)}
              </p>
              <h2 className="type-display-md mt-4 text-balance text-neutral-900">
                {t(k.bloodHeading)}
              </h2>
              <p className="type-body-lg mt-6 text-balance text-neutral-500">
                {t(k.bloodSubtitle)}
              </p>
            </div>

            <div className="mt-10">
              {(() => {
                type BloodCell =
                  | { kind: "text"; value: string }
                  | { kind: "check" }
                  | { kind: "cross" }

                const rows: {
                  crit: string
                  ahead: BloodCell
                  gp: BloodCell
                }[] = [
                  {
                    crit: t(k.bloodRow1Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "check" },
                  },
                  {
                    crit: t(k.bloodRow2Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow3Crit),
                    ahead: { kind: "text", value: t(k.bloodRow3Ahead) },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow4Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "check" },
                  },
                  {
                    crit: t(k.bloodRow5Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "text", value: t(k.bloodRow5Gp) },
                  },
                  {
                    crit: t(k.bloodRow6Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow7Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "check" },
                  },
                  {
                    crit: t(k.bloodRow8Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "check" },
                  },
                  {
                    crit: t(k.bloodRow9Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "text", value: t(k.bloodRow9Gp) },
                  },
                  {
                    crit: t(k.bloodRow10Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "text", value: t(k.bloodRow10Gp) },
                  },
                  {
                    crit: t(k.bloodRow11Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow12Crit),
                    ahead: { kind: "text", value: t(k.bloodRow12Ahead) },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow13Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow14Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow15Crit),
                    ahead: { kind: "text", value: t(k.bloodRow15Ahead) },
                    gp: { kind: "cross" },
                  },
                  {
                    crit: t(k.bloodRow16Crit),
                    ahead: { kind: "text", value: t(k.bloodRow16Ahead) },
                    gp: { kind: "text", value: t(k.bloodRow16Gp) },
                  },
                  {
                    crit: t(k.bloodRow17Crit),
                    ahead: { kind: "check" },
                    gp: { kind: "text", value: t(k.bloodRow17Gp) },
                  },
                ]

                const renderCell = (cell: BloodCell, highlighted: boolean) => {
                  if (cell.kind === "check") {
                    return <GreenCheck width={20} height={20} />
                  }
                  if (cell.kind === "cross") {
                    return (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        aria-hidden="true"
                      >
                        <circle cx="10" cy="10" r="9.5" fill="#d4d4d4" />
                        <path
                          d="M7 7l6 6M13 7l-6 6"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                    )
                  }
                  return (
                    <span
                      className={
                        highlighted
                          ? "type-body-lg text-light-green-600 font-medium"
                          : "type-body-lg text-neutral-600"
                      }
                    >
                      {cell.value}
                    </span>
                  )
                }

                const gridCols = "1.1fr 1.1fr 1.1fr"
                // Ahead column position within 3.3fr total — equal columns center the Ahead column on the page
                const aheadLeftPct = (1.1 / 3.3) * 100
                const aheadWidthPct = (1.1 / 3.3) * 100

                return (
                  // Single table with sticky first column on narrow viewports.
                  // Background of the empty header cell + sticky-column shadow
                  // match the imaging table above for a consistent UX.
                  <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
                    <div className="relative min-w-[640px]">
                      {/* Header row */}
                      <div
                        className="grid"
                        style={{ gridTemplateColumns: gridCols }}
                      >
                        <div />
                        <div className="bg-light-green-500 type-body-lg rounded-t-[16px] px-3 py-5 text-center text-white">
                          {t(k.bloodColAhead)}
                        </div>
                        <div className="type-body-lg px-3 py-5 text-center text-neutral-900">
                          {t(k.bloodColGp)}
                        </div>
                      </div>

                      {/* Rows */}
                      <div>
                        {rows.map((row, i) => {
                          const rowBg =
                            i % 2 === 0 ? "bg-neutral-50" : "bg-white"
                          return (
                            <div
                              key={i}
                              className="grid"
                              style={{ gridTemplateColumns: gridCols }}
                            >
                              <div
                                className={`type-body-lg ${rowBg} flex items-center justify-start rounded-l-2xl px-4 py-4 text-left text-neutral-600`}
                              >
                                {row.crit}
                              </div>
                              <div
                                className={`${rowBg} flex items-center justify-center px-3 py-4 text-center`}
                              >
                                {renderCell(row.ahead, true)}
                              </div>
                              <div
                                className={`${rowBg} flex items-center justify-center rounded-r-2xl px-3 py-4 text-center`}
                              >
                                {renderCell(row.gp, false)}
                              </div>
                            </div>
                          )
                        })}
                      </div>

                      {/* Ahead column highlight overlay */}
                      <div
                        className="border-light-green-500 pointer-events-none absolute inset-y-0 rounded-[16px] border-2"
                        style={{
                          left: `${aheadLeftPct}%`,
                          width: `${aheadWidthPct}%`,
                        }}
                      />
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 7.5: Why the combination matters (imaging + blood) ─── */}
      <section
        aria-label="Why imaging and blood analysis matter together"
        className="py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-center text-neutral-400">
              {t(k.comboLabel)}
            </p>
            <h2 className="type-display-md mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
              {t(k.comboHeading)}
            </h2>
            <p className="type-body-lg mx-auto mt-8 max-w-3xl text-center text-balance text-neutral-700">
              “{t(k.comboQuote)}”
            </p>
            <div className="mt-6 flex items-center justify-center gap-2.5">
              <Image
                src={blobMediaUrl("/images/about/anna-erat.jpg")}
                alt="Dr. med. Anna Erat"
                width={28}
                height={28}
                className="h-7 w-7 rounded-full object-cover"
              />
              <p className="type-body-lg text-neutral-900">
                <span className="font-medium">Dr. med. Anna Erat, PhD</span>{" "}
                <span className="text-neutral-500">{t(k.comboAuthorRole)}</span>
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Card 1 — Additional insights (lavender) */}
            <div>
              <div
                className="flex h-full flex-col rounded-[24px] p-6 text-white md:p-8"
                style={{ backgroundColor: "#7B8EC7" }}
              >
                <h3 className="type-title-lg text-white">
                  {t(k.comboCard1Title)}
                </h3>
                <p className="type-body-lg mt-2 text-white/80">
                  {t(k.comboCard1Desc)}
                </p>
                <div className="mt-6 rounded-2xl bg-white p-5 shadow-[0_8px_24px_rgba(0,0,0,0.08)] md:mt-auto">
                  <div className="flex items-center gap-2.5">
                    <Image
                      src={blobMediaUrl("/images/report/dr-donati.png")}
                      alt="Dr. Olivio Donati"
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <p className="text-sm font-medium text-neutral-900">
                      Dr. Olivio Donati
                    </p>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-700">
                    {t(k.comboCard1DoctorQuote)}
                  </p>
                  <div className="mt-4 flex items-center gap-3 rounded-xl bg-neutral-50 p-2">
                    <Image
                      src={blobMediaUrl(
                        "/images/report/recs/additional-insights.jpg"
                      )}
                      alt=""
                      width={40}
                      height={40}
                      className="h-10 w-10 rounded-xl object-cover"
                    />
                    <span className="flex-1 text-sm text-neutral-700">
                      {t(k.comboCard1Actions)}
                    </span>
                    <ChevronRightIcon
                      width={14}
                      height={14}
                      className="text-neutral-400"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 — Clear signals (white + gauge) */}
            <div>
              <div className="flex h-full flex-col rounded-[24px] bg-white p-6 md:p-8">
                <h3 className="type-title-lg text-neutral-900">
                  {t(k.comboCard2Title)}
                </h3>
                <p className="type-body-lg mt-2 text-neutral-500">
                  {t(k.comboCard2Desc)}
                </p>
                <div className="mt-auto flex flex-col items-center pt-6">
                  <ClearSignalsGauge />
                  <p className="mt-2 text-base font-medium text-neutral-900">
                    {t(k.comboCard2Metric)}
                  </p>
                  <span className="bg-light-green-100 mt-3 rounded-full px-3 py-1 text-sm font-medium text-[#7B8C3B]">
                    {t(k.comboCard2Badge)}
                  </span>
                  <p className="mt-3 text-sm text-neutral-400">
                    {t(k.comboCard2Note)}
                  </p>
                </div>
              </div>
            </div>

            {/* Card 4 in display order — Follow-up tests (olive) */}
            <div className="order-4">
              <div
                className="flex h-full flex-col rounded-[24px] p-6 md:p-8"
                style={{ backgroundColor: "var(--color-light-green-400)" }}
              >
                <h3 className="type-title-lg text-white">
                  {t(k.comboCard3Title)}
                </h3>
                <p className="type-body-lg mt-2 text-white/85">
                  {t(k.comboCard3Desc)}
                </p>
                <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-white p-3 shadow-[0_8px_24px_rgba(0,0,0,0.08)] md:mt-auto">
                  <div className="flex items-center gap-3 p-2">
                    <PackageIcon variant="blue" size="sm" />
                    <div className="flex-1">
                      <p className="flex items-center gap-1 text-sm font-medium text-neutral-900">
                        Ahead Advanced
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-neutral-400"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
                        </svg>
                      </p>
                      <p className="text-xs text-neutral-500">
                        {t(k.comboCard3AddOns)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 rounded-xl bg-neutral-50 p-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#644F8B"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-neutral-900">
                        October 25th
                      </p>
                      <p className="text-xs text-neutral-500">9:00 – 10:00</p>
                    </div>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-neutral-700">
                      {t(k.comboCard3Status)}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 p-2">
                    <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-md bg-white">
                      <Image
                        src={blobMediaUrl(
                          "/images/report/hirslanden-zurich.png"
                        )}
                        alt=""
                        fill
                        sizes="40px"
                        className="object-contain"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900">
                        Radiologie Hirslanden Zürich
                      </p>
                      <p className="truncate text-xs text-neutral-500">
                        Witellikerstrasse 40, 8032 Zürich
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-around border-t border-neutral-100 pt-3">
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-medium text-neutral-700"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 22s-8-7.58-8-13a8 8 0 1 1 16 0c0 5.42-8 13-8 13Z" />
                        <circle cx="12" cy="9" r="3" />
                      </svg>
                      {t(k.comboCard3Directions)}
                    </button>
                    <div className="h-4 w-px bg-neutral-200" />
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-xs font-medium text-neutral-700"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      {t(k.comboCard3Calendar)}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Card 3 in display order — Imaging insight (dark, spine MRI side-by-side) */}
            <div className="order-3">
              <div
                className="flex h-full flex-col overflow-hidden rounded-[24px]"
                style={{ backgroundColor: "var(--color-foreground)" }}
              >
                <div className="p-6 md:p-8">
                  <h3 className="type-title-lg text-white">
                    {t(k.comboCard4Title)}
                  </h3>
                  <p className="type-body-lg mt-2 mb-4 text-white/70">
                    {t(k.comboCard4Desc)}
                  </p>
                </div>
                <div className="flex flex-1 gap-3 px-6 pb-6">
                  <m.div
                    initial={{ opacity: 0, x: -40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.7,
                      ease: [0.16, 1, 0.3, 1],
                      delay: 0.3,
                    }}
                    className="relative flex w-1/2 items-center justify-center overflow-hidden rounded-[16px] bg-black"
                  >
                    {/* Inner wrapper locked to the source image's aspect ratio so
                        the overlay circles always land on the same anatomy
                        regardless of the panel's outer dimensions. */}
                    <div
                      className="relative w-full"
                      style={{ aspectRatio: "864 / 1076" }}
                    >
                      <Image
                        src={blobMediaUrl(
                          "/images/report/spine-comparison-real.png"
                        )}
                        alt="Spine MRI 2022"
                        fill
                        className="object-contain"
                      />
                      <div className="border-terracotta-300 absolute top-[47%] left-[55%] h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2" />
                      <div className="border-terracotta-300 absolute top-[82%] left-[55%] h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2" />
                    </div>
                    <div className="absolute top-3 left-3 z-10">
                      <div className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                        2022
                      </div>
                    </div>
                  </m.div>
                  <m.div
                    initial={{ opacity: 0, x: 40 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{
                      duration: 0.7,
                      ease: [0.16, 1, 0.3, 1],
                      delay: 0.5,
                    }}
                    className="relative flex w-1/2 items-center justify-center overflow-hidden rounded-[16px] bg-black"
                  >
                    <div
                      className="relative w-full"
                      style={{ aspectRatio: "864 / 1076" }}
                    >
                      <Image
                        src={blobMediaUrl(
                          "/images/report/spine-comparison-real.png"
                        )}
                        alt="Spine MRI 2025"
                        fill
                        className="object-contain"
                      />
                      <div className="border-terracotta-300 absolute top-[82%] left-[55%] h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2" />
                    </div>
                    <div className="absolute top-3 right-3 z-10">
                      <div className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                        2025
                      </div>
                    </div>
                  </m.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Six factors ─── */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <h2 className="type-display-sm max-w-3xl text-balance text-neutral-900">
              {t(k.factorsHeading)}
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: <BodyFigureIcon size={28} className="text-neutral-900" />,
                title: k.factor1Title,
                body: k.factor1Body,
              },
              {
                icon: <ChecklistIcon size={28} className="text-neutral-900" />,
                title: k.factor2Title,
                body: k.factor2Body,
              },
              {
                icon: <Report2Icon size={28} className="text-neutral-900" />,
                title: k.factor3Title,
                body: k.factor3Body,
              },
              {
                icon: <ReportIcon size={28} className="text-neutral-900" />,
                title: k.factor4Title,
                body: k.factor4Body,
              },
              {
                icon: <FollowUpIcon size={28} className="text-neutral-900" />,
                title: k.factor5Title,
                body: k.factor5Body,
              },
              {
                icon: <ArrowPathIcon size={28} className="text-neutral-900" />,
                title: k.factor6Title,
                body: k.factor6Body,
              },
            ].map((factor) => (
              <div key={factor.title}>
                {factor.icon}
                <h3 className="type-title-lg mt-4 text-neutral-900">
                  {t(factor.title)}
                </h3>
                <p className="type-body-lg mt-3 text-neutral-600">
                  {t(factor.body)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Why Ahead Health ─── */}
      <section className="pb-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          {/* Row 1 — Why card wider than Locations (~60/40) */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[3fr_2fr]">
            {/* Card 1 — Why Ahead Health */}
            <div>
              <div className="flex h-full min-h-[480px] flex-col rounded-[24px] bg-white p-8 md:p-12">
                <div className="bg-aubergine-900 inline-flex h-11 w-11 items-center justify-center rounded-[10px]">
                  <Image
                    src="/icons/ahead-logo.svg"
                    alt=""
                    width={20}
                    height={22}
                  />
                </div>
                <h3 className="type-display-md mt-8 text-neutral-900">
                  {t(k.whyAhTitle)}
                </h3>
                <div className="mt-auto pt-16">
                  <p className="type-title-lg text-neutral-900">
                    {t(k.whyAhStat)}
                  </p>
                  <p className="type-body-lg mt-3 max-w-md text-neutral-600">
                    {t(k.whyAhBody)}
                  </p>
                  <div className="mt-8 flex flex-col items-start gap-4 opacity-60 sm:flex-row sm:items-center sm:gap-6">
                    <Image
                      src={blobMediaUrl("/images/logos/oxford.svg")}
                      alt="University of Oxford"
                      width={100}
                      height={28}
                      className="h-7 w-auto"
                    />
                    <Image
                      src={blobMediaUrl("/images/logos/karolinska.svg")}
                      alt="Karolinska Institutet"
                      width={88}
                      height={28}
                      className="h-7 w-auto"
                    />
                    <Image
                      src={blobMediaUrl("/images/logos/st-gallen.svg")}
                      alt="University of St. Gallen"
                      width={100}
                      height={28}
                      className="h-7 w-auto"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2 — Locations */}
            <div>
              <div className="bg-aubergine-900 relative flex h-full min-h-[480px] [transform:translateZ(0)] flex-col overflow-hidden rounded-[24px] p-8 text-white md:p-10">
                <div>
                  <h3 className="type-display-md">{t(k.whyLocTitle)}</h3>
                  <p className="type-body-lg mt-4 max-w-md text-white/80">
                    {t(k.whyLocBody)}
                  </p>
                  <Link
                    href={lp("/locations")}
                    className="mt-6 block w-full sm:inline-block sm:w-auto"
                  >
                    <Button
                      variant="outline"
                      className="w-full border-white/30 bg-transparent text-white hover:border-white/40 hover:bg-white/10 sm:w-auto"
                    >
                      {t(k.whyLocSeeAll)}
                    </Button>
                  </Link>
                </div>
                <div className="relative mt-auto -mr-9 -mb-9 overflow-hidden rounded-tl-[24px] bg-white p-6 pb-12 text-neutral-900 md:-mr-11 md:-mb-11">
                  <p className="type-label-sm tracking-wide text-neutral-400 uppercase">
                    {t(k.whyLocAll)}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {[
                      "Zürich",
                      "Thalwil",
                      "Geneva",
                      "Basel",
                      "Rheinfelden",
                      "St. Gallen",
                    ].map((city) => (
                      <span
                        key={city}
                        className="type-body-sm inline-flex items-center rounded-full border border-neutral-200 px-3 py-1 text-neutral-800"
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                  {/* Fade gradient — affordance suggesting more locations */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Row 2 — portrait narrower, report wider */}
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-[5fr_8fr]">
            {/* Card 3 — Portrait */}
            <div>
              <div className="relative h-full min-h-[480px] overflow-hidden rounded-[24px]">
                <Image
                  src={blobMediaUrl(
                    "/images/how-to-choose/why-portrait-man.jpg"
                  )}
                  alt={t(k.whyPortraitManAlt)}
                  fill
                  sizes="(min-width: 768px) 38vw, 100vw"
                  className="scale-x-[-1] object-cover"
                />
              </div>
            </div>

            {/* Card 4 — Integrated report (reuses report-page tablet stat-card design) */}
            <div>
              <div className="flex h-full flex-col rounded-[24px] bg-white p-6 md:p-8">
                <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 md:p-5">
                  {/* Biological age — image bg + progress bar with pill */}
                  <div className="relative flex flex-col justify-between overflow-hidden rounded-[18px] p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
                    <Image
                      src={blobMediaUrl(
                        "/images/report/backgrounds/summary_bg_1.png"
                      )}
                      alt=""
                      fill
                      className="z-0 object-cover object-center"
                      aria-hidden="true"
                    />
                    <div className="relative z-10">
                      <CountUp
                        target={24}
                        className="type-display-sm leading-none font-medium text-white"
                      />
                      <p className="type-body-sm mt-1 text-white">
                        {tReport("bioAgeLabel")}
                      </p>
                      <div className="relative mt-4 h-2 w-full overflow-visible rounded-full bg-white/30">
                        <div
                          className="absolute top-0 bottom-0 rounded-full"
                          style={{
                            left: "25%",
                            right: "35%",
                            background:
                              "repeating-linear-gradient(45deg, rgba(255,255,255,0.5), rgba(255,255,255,0.5) 2px, transparent 2px, transparent 5px)",
                          }}
                        />
                        <div
                          className="absolute top-[-2px] bottom-[-2px] w-[2px] rounded-full bg-white"
                          style={{ left: "65%" }}
                        />
                        <div
                          className="absolute top-1/2 flex -translate-y-1/2 items-center gap-0.5 rounded-full bg-white px-1.5 py-0.5 shadow-sm"
                          style={{ left: "calc(25% - 2px)" }}
                        >
                          <svg
                            width="8"
                            height="8"
                            viewBox="0 0 10 10"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M5 1v8M2 6l3 3 3-3" />
                          </svg>
                          <span className="type-label-sm font-medium text-neutral-900">
                            11 {tReport("yrsLabel")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="type-label-sm relative z-10 mt-4 leading-snug text-white md:text-xs">
                      {tReport("bioAgeDesc")}
                    </p>
                  </div>

                  {/* Biomarkers — segmented bar + pills */}
                  <div className="flex flex-col justify-between rounded-[18px] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
                    <div>
                      <CountUp
                        target={89}
                        className="type-display-sm leading-none font-medium text-neutral-900"
                      />
                      <p className="type-body-sm mt-1 text-neutral-800">
                        {tReport("biomarkersLabelShort")}
                      </p>
                    </div>
                    <div className="mt-4">
                      <div className="flex h-1.5 items-stretch gap-1">
                        <div
                          className="bg-deep-green-500 rounded-full"
                          style={{ width: "13.5%" }}
                        />
                        <div
                          className="bg-bright-green-500 rounded-full"
                          style={{ width: "82%" }}
                        />
                        <div
                          className="bg-terracotta-300 rounded-full"
                          style={{ width: "4.5%" }}
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                          <span className="font-medium">12</span>
                          <span className="text-neutral-600">
                            {tReport("optimalLower")}
                          </span>
                        </span>
                        <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                          <span className="font-medium">73</span>
                          <span className="text-neutral-600">
                            {tReport("inRangeLower")}
                          </span>
                        </span>
                        <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                          <span className="font-medium">4</span>
                          <span className="text-neutral-600">
                            {tReport("outOfRangeLower")}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Findings — dot pills */}
                  <div className="flex flex-col justify-between rounded-[18px] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
                    <div>
                      <CountUp
                        target={10}
                        className="type-display-sm leading-none font-medium text-neutral-900"
                      />
                      <p className="type-body-sm mt-2 text-neutral-800">
                        {tReport("findingsLabel")}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                        <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" />
                        <span className="font-medium">1</span>
                        <span className="text-neutral-600">
                          {tReport("noFindingSingular")}
                        </span>
                      </span>
                      <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                        <span className="bg-deep-green-500 h-1.5 w-1.5 rounded-full" />
                        <span className="font-medium">5</span>
                        <span className="text-neutral-600">
                          {tReport("observation")}
                        </span>
                      </span>
                      <span className="type-label-sm inline-flex items-center gap-1 rounded-full border border-neutral-200 px-2 py-0.5 text-neutral-800">
                        <span className="bg-bright-green-500 h-1.5 w-1.5 rounded-full" />
                        <span className="font-medium">4</span>
                        <span className="text-neutral-600">
                          {tReport("minor")}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Radiology findings — stacked MRI panels */}
                  <div className="flex flex-col items-center justify-between rounded-[18px] bg-white p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
                    <span className="type-body-sm self-start text-neutral-800">
                      {tReport("radiologyFindingsLabel")}
                    </span>
                    <div className="relative my-3 h-16 w-16">
                      <m.div
                        initial={{ rotate: 0, x: 0, y: 0 }}
                        whileInView={{ rotate: -10, x: -10, y: 3 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                          duration: 0.9,
                          delay: 0.25,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute inset-0 rounded-2xl border border-white bg-neutral-900 shadow-md"
                      />
                      <m.div
                        initial={{ rotate: 0, x: 0, y: 0 }}
                        whileInView={{ rotate: 10, x: 10, y: 3 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                          duration: 0.9,
                          delay: 0.35,
                          ease: [0.16, 1, 0.3, 1],
                        }}
                        className="absolute inset-0 rounded-2xl border border-white bg-neutral-900 shadow-md"
                      />
                      <div className="absolute inset-0 overflow-hidden rounded-2xl border border-white bg-neutral-900 shadow-md">
                        <Image
                          src={blobMediaUrl("/images/report/brain-scan.png")}
                          alt=""
                          fill
                          sizes="64px"
                          className="object-cover"
                        />
                      </div>
                    </div>
                    <p className="text-sm font-medium text-neutral-900">
                      <CountUp target={10} className="font-medium" />{" "}
                      {tReport("findingsSuffix")}
                    </p>
                  </div>
                </div>
                <div className="mt-auto pt-10 md:pt-12">
                  <h3 className="type-display-sm text-neutral-900">
                    {t(k.whyReportTitle)}
                  </h3>
                  <p className="type-body-lg mt-3 text-neutral-600">
                    {t(k.whyReportBody)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 5: Who reads your scan — reused from homepage ─── */}
      <DoctorsSection
        initialExperts={initialExperts}
        heading={
          <div className="mx-auto mb-10 max-w-[1440px] px-4 md:px-10">
            <div className="hidden md:flex md:items-start md:justify-between md:gap-12">
              <h2 className="type-display-sm max-w-md text-neutral-900">
                {tHome(hk.expertsHeading)}
              </h2>
              <p className="type-body-lg max-w-md text-right text-neutral-600">
                {tHome(hk.expertsSub)}
              </p>
            </div>
            <div className="text-center md:hidden">
              <h2 className="type-display-md text-neutral-900">
                {tHome(hk.expertsHeading)}
              </h2>
              <p className="type-body-lg mt-4 text-neutral-600">
                {tHome(hk.expertsSub)}
              </p>
              <Link
                href={lp("/about")}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border border-neutral-200 py-4 text-neutral-900 transition-colors hover:bg-neutral-50"
              >
                {tHome(hk.doctorsLink)} <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        }
      />

      {/* ─── Section 6: What you get back (report comparison) ─── */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-center text-neutral-400">
              {t(k.compareLabel)}
            </p>
            <h2 className="type-display-md mx-auto mt-4 max-w-3xl text-center text-balance text-neutral-900">
              {t(k.compareHeading)}
            </h2>
          </div>

          <div>
            <div className="mt-12 rounded-[24px] bg-neutral-100 p-4 md:p-6">
              {/* Mobile stack — content + iPhone mockups */}
              <div className="flex flex-col gap-4 md:hidden">
                <div className="overflow-hidden rounded-[20px] bg-white">
                  <div className="p-8">
                    <h3 className="type-title-lg text-neutral-900">
                      {t(k.compareAheadTitle)}
                    </h3>
                    <ul className="mt-8 space-y-4">
                      {[
                        k.compareAheadItem1,
                        k.compareAheadItem2,
                        k.compareAheadItem3,
                        k.compareAheadItem4,
                        k.compareAheadItem5,
                        k.compareAheadItem6,
                      ].map((item) => (
                        <li
                          key={item}
                          className="type-body-lg flex items-center gap-3 text-neutral-800"
                        >
                          <GreenCheck className="shrink-0" />
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      <Link
                        href={lp("/report")}
                        className="block w-full sm:inline-block sm:w-auto"
                      >
                        <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative w-full overflow-hidden text-white sm:w-auto">
                          <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                            {t(k.compareAheadCta)}
                          </span>
                          <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                            <ArrowRightIcon size={18} />
                          </span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="px-4 pb-4">
                    <ReportMockupIphone />
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="type-title-lg text-neutral-900">
                    {t(k.compareTypicalTitle)}
                  </h3>
                  <ul className="mt-8 space-y-4">
                    {[
                      k.compareTypicalItem1,
                      k.compareTypicalItem2,
                      k.compareTypicalItem3,
                      k.compareTypicalItem4,
                      k.compareTypicalItem5,
                      k.compareTypicalItem6,
                    ].map((item) => (
                      <li
                        key={item}
                        className="type-body-lg flex items-start gap-3 text-neutral-500"
                      >
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-200">
                          <CloseIcon
                            width={10}
                            height={10}
                            className="text-neutral-500"
                          />
                        </span>
                        <span>{t(item)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="relative mx-auto w-full max-w-[340px]">
                  <div
                    className="relative rounded-[48px] p-[6px]"
                    style={{
                      background:
                        "linear-gradient(180deg, #1e1e1e 0%, #0a0a0a 50%, #1e1e1e 100%)",
                      boxShadow: [
                        "inset 0 1px 0 rgba(255,255,255,0.08)",
                        "inset 0 -1px 0 rgba(0,0,0,0.5)",
                        "0 1px 2px rgba(0,0,0,0.05)",
                        "0 10px 30px -6px rgba(0,0,0,0.14)",
                        "0 40px 80px -24px rgba(0,0,0,0.22)",
                      ].join(", "),
                    }}
                  >
                    <div
                      className="relative overflow-hidden rounded-[42px] bg-neutral-100"
                      style={{
                        aspectRatio: "9 / 19.5",
                        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.04)",
                      }}
                    >
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute top-2 left-1/2 z-30 h-6 w-24 -translate-x-1/2 rounded-full bg-black"
                      />
                      {/* Horizontally scrollable so the value/unit/range columns
                          are reachable inside the narrow iPhone frame. */}
                      <div
                        className="absolute inset-0 overflow-x-auto overflow-y-hidden"
                        style={{ WebkitOverflowScrolling: "touch" }}
                      >
                        <div
                          className="relative h-full"
                          style={{ aspectRatio: "1467 / 1072" }}
                        >
                          <Image
                            src={blobMediaUrl(
                              "/images/how-to-choose/typical-patient-report.png"
                            )}
                            alt={t(k.compareTypicalTitle)}
                            fill
                            sizes="800px"
                            className="object-cover object-top"
                          />
                        </div>
                      </div>
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute bottom-2 left-1/2 z-30 h-[5px] w-[100px] -translate-x-1/2 rounded-full bg-black/80"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop 2-col layout — each column owns its content + mockup. Mockups are absolutely
                  positioned at the bottom and clipped by the column's overflow-hidden, producing the
                  cut-off treatment in the reference. */}
              <div className="hidden md:grid md:grid-cols-2 md:gap-6">
                {/* LEFT — Ahead Health, white background extends full column */}
                <div className="relative h-[820px] overflow-hidden rounded-[20px] bg-white">
                  <div className="p-10">
                    <h3 className="type-title-lg text-neutral-900">
                      {t(k.compareAheadTitle)}
                    </h3>
                    <ul className="mt-8 space-y-4">
                      {[
                        k.compareAheadItem1,
                        k.compareAheadItem2,
                        k.compareAheadItem3,
                        k.compareAheadItem4,
                        k.compareAheadItem5,
                        k.compareAheadItem6,
                      ].map((item) => (
                        <li
                          key={item}
                          className="type-body-lg flex items-center gap-3 text-neutral-800"
                        >
                          <GreenCheck className="shrink-0" />
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8">
                      <Link href={lp("/report")} className="inline-block">
                        <Button className="group bg-aubergine-800 hover:bg-aubergine-900 relative overflow-hidden text-white">
                          <span className="inline-block transition-transform duration-200 ease-out group-hover:-translate-x-2">
                            {t(k.compareAheadCta)}
                          </span>
                          <span className="absolute right-4 translate-x-3 opacity-0 transition-all duration-200 ease-out group-hover:translate-x-0 group-hover:opacity-100">
                            <ArrowRightIcon size={18} />
                          </span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                  <div className="absolute inset-x-10 top-[62%]">
                    <ReportMockupIpad
                      compact
                      contentScale={0.5}
                      explainers={{
                        selector: tReport("explainerSelector"),
                        nav: tReport("explainerNav"),
                        bioAge: tReport("explainerBioAge"),
                        heartRisk: tReport("explainerHeartRisk"),
                        biomarkers: tReport("explainerBiomarkers"),
                        findings: tReport("explainerFindings"),
                        summary: tReport("explainerSummary"),
                        sidebarSummary: tReport("explainerSidebarSummary"),
                      }}
                    />
                  </div>
                </div>

                {/* RIGHT — Typical, no background (inherits the neutral-100 frame) */}
                <div className="relative h-[820px] overflow-hidden rounded-[20px]">
                  <div className="p-10">
                    <h3 className="type-title-lg text-neutral-900">
                      {t(k.compareTypicalTitle)}
                    </h3>
                    <ul className="mt-8 space-y-4">
                      {[
                        k.compareTypicalItem1,
                        k.compareTypicalItem2,
                        k.compareTypicalItem3,
                        k.compareTypicalItem4,
                        k.compareTypicalItem5,
                        k.compareTypicalItem6,
                      ].map((item) => (
                        <li
                          key={item}
                          className="type-body-lg flex items-start gap-3 text-neutral-500"
                        >
                          <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-neutral-200">
                            <CloseIcon
                              width={10}
                              height={10}
                              className="text-neutral-500"
                            />
                          </span>
                          <span>{t(item)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="absolute inset-x-10 bottom-0 translate-y-[28%]">
                    {/* Paper sheets — front sheet is the rendered HTML report; back sheet provides depth. */}
                    <div className="relative mx-auto aspect-[4/3] w-full">
                      <div
                        aria-hidden="true"
                        className="absolute top-6 right-[-20px] bottom-[-14px] left-6 overflow-hidden rounded-[2px] bg-white"
                        style={{
                          boxShadow:
                            "0 1px 2px rgba(0,0,0,0.06), 0 8px 24px -6px rgba(0,0,0,0.14), 0 24px 48px -16px rgba(0,0,0,0.18)",
                        }}
                      />
                      <div
                        className="absolute inset-0 overflow-hidden rounded-[2px] bg-white"
                        style={{
                          boxShadow:
                            "0 1px 2px rgba(0,0,0,0.06), 0 12px 32px -8px rgba(0,0,0,0.18), 0 32px 64px -20px rgba(0,0,0,0.22)",
                        }}
                      >
                        <TypicalReportMockup
                          t={t as unknown as (key: string) => string}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 7: Insurance coverage ─── */}
      <div className="-my-8 md:-my-12">
        <InsuranceSection trackingLocation="compare-health-check-ups-switzerland-insurance" />
      </div>

      {/* ─── Section 8: Provider checklist ─── */}
      <section aria-label="Provider checklist" className="py-20 md:py-28">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <div>
            <p className="type-label-lg text-neutral-400">
              {t(k.providerLabel)}
            </p>
            <h2 className="type-display-sm mt-4 max-w-3xl text-balance text-neutral-900">
              {t(k.providerHeading)}
            </h2>
            <p className="type-body-lg mt-4 max-w-2xl text-neutral-500">
              {t(k.providerSubtitle)}
            </p>
          </div>

          <div>
            <div className="mt-12 overflow-hidden rounded-[20px] border border-neutral-200">
              <ul className="grid grid-cols-1 md:grid-cols-2">
                {[
                  { title: k.providerItem1Title, body: k.providerItem1Body },
                  { title: k.providerItem2Title, body: k.providerItem2Body },
                  { title: k.providerItem3Title, body: k.providerItem3Body },
                  { title: k.providerItem4Title, body: k.providerItem4Body },
                  { title: k.providerItem5Title, body: k.providerItem5Body },
                  { title: k.providerItem6Title, body: k.providerItem6Body },
                  { title: k.providerItem7Title, body: k.providerItem7Body },
                  { title: k.providerItem8Title, body: k.providerItem8Body },
                ].map((item, i, arr) => {
                  const isLastItem = i === arr.length - 1
                  const isLastRowDesktop = i >= arr.length - 2
                  const isRightCol = i % 2 === 1
                  return (
                    <li
                      key={item.title}
                      className={[
                        "flex gap-4 p-6",
                        // Mobile: every item except the last gets a bottom border.
                        !isLastItem ? "border-b border-neutral-200" : "",
                        // Desktop: items in the last row of the 2-col grid drop their bottom border.
                        isLastRowDesktop && !isLastItem ? "md:border-b-0" : "",
                        !isRightCol ? "md:border-r md:border-neutral-200" : "",
                      ].join(" ")}
                    >
                      <CheckmarkIcon className="mt-1 shrink-0 text-neutral-900" />
                      <div>
                        <h3 className="type-title-lg text-neutral-900">
                          {t(item.title)}
                        </h3>
                        <p className="type-body-lg mt-2 text-neutral-500">
                          {t(item.body)}
                        </p>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 9: Testimonials — shared carousel, how-it-works reviews ─── */}
      <TestimonialsSection cmsTestimonials={initialTestimonials} />

      {/* ─── Section 9.5: FAQ — page-specific items from CMS ─── */}
      <FaqSection
        heading={tHiw(hiwk.faqHeading)}
        items={faqItems}
        linkLabel={null}
        allowMultiple
        currentPage="compare-health-check-ups-switzerland"
      />

      {/* ─── Section 10: Bottom CTA ─── */}
      <div>
        <CtaBanner
          title={t(k.ctaHeading)}
          subtitle={t(k.ctaSubtitle)}
          imageSrc={blobMediaUrl("/images/how-to-choose/cta-couple.jpg")}
          overlay="gradient"
          className="md:min-h-[500px]"
          imageClassName="origin-top scale-[1.15] object-[center_45%]"
          fullWidthCtasMobile
          primaryCta={{ label: t(k.ctaBook), href: lp("/pricing") }}
          secondaryCta={{ label: t(k.ctaTalk), href: lp("/free-call") }}
          trustItems={[
            { icon: <SwissShieldIcon size={18} />, label: t(k.ctaTrust1) },
            {
              icon: <CheckCircleOutlineIcon size={18} />,
              label: t(k.ctaTrust2),
            },
            {
              icon: <CheckCircleOutlineIcon size={18} />,
              label: t(k.ctaTrust4),
            },
          ]}
        />
      </div>
    </>
  )
}
